import React, { useState } from 'react'

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material'
import RelatedScreen from '../../../Components/RelatedScreen'
import { useAddSampleContext } from '../../../Contexts/Modules/SampleModule/AddSampleModule'
import { useGlobalContext } from '../../../Contexts/GlobalTriggers'
import SampleIDInput from './SampleIDInput'
import {
  useGetAnimalsInContainers,
  useNewScreenSample,
} from '../../../APIs/Hooks/SampleHooks'
import ContainerCard from './Cards/ContainerCard'
import PageSection from '../../../Constants&Theme/PageSection'
import { stackGap } from '../../../Constants&Theme/Spacing'
import InvalidAccessAlert from './InvalidAccessAlert'

function AddContainerSample() {
  const { playScanSound } = useGlobalContext()
  const { sampleRun } = useAddSampleContext()

  const [containerKeys, setContainerKeys] = useState<any[]>([])
  const [animals, setAnimals] = useState<any[]>([])
  const [sampleID, setSampleID] = useState<string>('')
  const [validID, setValidID] = useState<boolean>(false)

  const containerQueries = useGetAnimalsInContainers(
    [...new Set(containerKeys)],
    setContainerKeys,
  )
  const { mutate: saveSample } = useNewScreenSample(setAnimals)

  function handleChangeCheckbox() {
    const animalReset: any = {}
    if (Object.values(animals).filter((item) => item).length > 0) {
      containerQueries.forEach((q) => {
        q.data?.animals.forEach((item: any) => (animalReset[item.UAI] = false))
      })
    } else {
      containerQueries.forEach((q) => {
        q.data?.animals.forEach((item: any) => (animalReset[item.UAI] = true))
      })
    }
    setAnimals(animalReset)
  }

  if (!sampleRun) {
    return <InvalidAccessAlert />
  }
  return (
    <Box
      sx={{
        maxWidth: 'md',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <RelatedScreen sampleRun={sampleRun} />
      <PageSection title={'Containers'}>
        <Stack
          gap={stackGap.col}
          width={1}
          alignItems={'center'}
          sx={{ display: 'flex' }}
        >
          <Autocomplete
            multiple
            value={containerKeys}
            onChange={(event, newValue) => {
              playScanSound(true)
              setContainerKeys(newValue)
            }}
            options={[]}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scan Containers"
                placeholder="Container ID"
                onKeyDown={(e: any) => {
                  if (
                    (e.code === 'Enter' || e.code === 'Tab') &&
                    e.target.value
                  ) {
                    playScanSound(true)
                    setContainerKeys((ck) => ck.concat(e.target.value))
                  }
                }}
              />
            )}
            fullWidth
          />
          {containerQueries.length > 0 && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      Object.values(animals).filter((item) => item).length > 0
                    }
                    onChange={handleChangeCheckbox}
                    color={'primary'}
                  />
                }
                label="Select / Deselect All"
                labelPlacement="start"
                sx={{ ml: 'auto', display: 'flex' }}
              />
              {containerQueries.map((containerQuery, index) => (
                <ContainerCard
                  key={index}
                  containerQuery={containerQuery}
                  toggleCollect={(e: any) => {
                    setAnimals((a: any) => ({
                      ...a,
                      [e.target.name]: !a[e.target.name],
                    }))
                  }}
                  animals={animals}
                  index={index}
                />
              ))}
              <SampleIDInput
                disabled={
                  containerQueries.filter((c) => c.data?.container).length === 0
                }
                value={sampleID}
                valid={validID}
                setValue={(e: any) => {
                  setValidID(false)
                  setSampleID(e?.target?.value ?? '')
                }}
                setValidID={setValidID}
              />
              <Button
                fullWidth
                variant="contained"
                color="success"
                disabled={
                  !(
                    Object.values(animals).filter((item) => item).length > 0 &&
                    validID
                  )
                }
                onClick={() => {
                  saveSample({
                    UAI: Object.entries(animals)
                      .filter(([, value]) => value)
                      .map(([key]) => key),
                    SampleID: sampleID,
                  })
                }}
              >
                Save Sample
              </Button>
            </>
          )}
        </Stack>
      </PageSection>
    </Box>
  )
}

export default AddContainerSample
